import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { LevStandardQueryMeta } from '../../../web/ReactQueryProvider';

export const ADMIN_ACCOUNT_CONTACT_AUTOCOMPLETE_QUERY_KEY = 'admin/account/contacts/autocomplete';

/**
 * Resolves a display label for a contact autocomplete result. The API returns a full
 * {@link Api.ContactReference} (name + emails) and leaves the name/email fallback to the client:
 * prefer first + last name, otherwise fall back to the primary email so support can still
 * disambiguate contacts that have no name on file.
 */
export function getContactReferenceDisplayName(contact: Api.ContactReference): string {
	const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
	if (name) {
		return name;
	}
	return contact.emailAddresses?.[0]?.value ?? '';
}

/**
 * Fetches contact suggestions from the admin account-scoped autocomplete endpoint.
 * Scoped to the viewed account (via accountId), not the signed-in admin's own contacts.
 * Disabled automatically when fragment is empty or whitespace.
 */
export function useAdminAccountContactAutocompleteQuery({
	accountId,
	fragment,
	pageSize = 20,
	enabled = true,
	meta,
}: {
	accountId: string;
	fragment: string;
	pageSize?: number;
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	return useQuery<Api.IPagedCollection<Api.ContactReference>>({
		enabled: enabled && !!accountId && fragment.trim().length > 0,
		meta,
		queryFn: () => {
			const params = new URLSearchParams();
			params.set('fragment', fragment);
			params.set('pageSize', String(pageSize));
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.ContactReference>>(
				`admin/account/${encodeURIComponent(accountId)}/contacts/autocomplete?${params.toString()}`,
				'GET'
			);
		},
		queryKey: [ADMIN_ACCOUNT_CONTACT_AUTOCOMPLETE_QUERY_KEY, accountId, fragment, pageSize],
	});
}
